// Contrato de autorização: api/docs/PROJECT_CONTEXT.md (Planning / Activities).
export const statuses = {
    1: 'Planejado',
    2: 'Em andamento',
    3: 'Pausado',
    4: 'Concluído',
    5: 'Cancelado',
}
export const priorities = { 1: 'Baixa', 2: 'Média', 3: 'Alta', 4: 'Urgente' }
export const normalizeText = (value) =>
    String(value ?? '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()
export function canPlanning(company, action = 'activity_view') {
    const roles = [company?.system_role, company?.company_role].map(normalizeText)
    if (
        roles.some((role) =>
            [
                'super',
                'super_admin',
                'sys_admin',
                'p2u',
                'adm',
                'company_admin',
                'admin_company',
            ].includes(role),
        )
    )
        return true
    const profiles = new Set((company?.access || [])
        .filter((permission) => ['planning', 'planning_execution', 'planning/project', 'planning_project']
            .includes(normalizeText(permission.mod_name)))
        .map((permission) => normalizeText(permission.action_name)))
    if (profiles.has('planning_manager')) return true
    const executorActions = new Set(['planning_user', 'project_view', 'activity_view',
        'activity_history', 'activity_start', 'activity_pause', 'activity_resume',
        'activity_finish', 'activity_manual_entry'])
    return profiles.has('planning_user') && executorActions.has(action)
}

// O endpoint de execução retorna subatividades aninhadas. Mantém os códigos do backend e o
// agrupamento por projeto, exibindo a subatividade executável (ex.: 3.01) no nível principal
// e permitindo expandir para visualizar sua atividade de origem (ex.: 3.0).
export function timeLoggingHierarchy(items = []) {
    if (!Array.isArray(items) || !items.length) return []
    if (items.every((item) => item && item.tree_id)) return items

    const byId = new Map()
    const childMap = new Map()
    const reverseCode = (a, b) =>
        String(b.code || b.level || '').localeCompare(
            String(a.code || a.level || ''),
            'pt-BR',
            { numeric: true },
        )

    function collect(list, parentId = null) {
        for (const raw of list || []) {
            if (!raw || Number(raw.status) === 6) continue
            const id = String(raw.id)
            const resolvedParentId =
                raw.parent_activity_id != null ? raw.parent_activity_id : parentId
            const nested = Array.isArray(raw.children)
                ? raw.children.filter((child) => child && Number(child.status) !== 6)
                : []
            const { children: ignored, ...data } = raw
            if (!byId.has(id)) {
                byId.set(id, {
                    ...data,
                    parent_activity_id: resolvedParentId,
                    level: raw.code || raw.level || '',
                })
            }
            if (resolvedParentId != null && String(resolvedParentId) !== id) {
                const parentKey = String(resolvedParentId)
                if (!childMap.has(parentKey)) childMap.set(parentKey, [])
                const siblings = childMap.get(parentKey)
                if (!siblings.some((sibling) => String(sibling.id) === id)) {
                    siblings.push(byId.get(id))
                }
            }
            if (nested.length) collect(nested, raw.id)
        }
    }

    collect(items)

    for (const item of byId.values()) {
        const parentId = item.parent_activity_id
        if (parentId != null && byId.has(String(parentId)) && String(parentId) !== String(item.id)) {
            const parentKey = String(parentId)
            if (!childMap.has(parentKey)) childMap.set(parentKey, [])
            const siblings = childMap.get(parentKey)
            if (!siblings.some((sibling) => String(sibling.id) === String(item.id))) {
                siblings.push(item)
            }
        }
    }

    for (const item of byId.values()) {
        const liveChildren = childMap.get(String(item.id)) || []
        item.hasChildren = Boolean(
            item.has_children ?? item.hasChildren ?? liveChildren.length > 0,
        )
    }

    const result = []
    const projects = new Map()
    for (const item of byId.values()) {
        if ((childMap.get(String(item.id)) || []).length > 0) continue
        const key = String(item.project_id ?? '')
        if (!projects.has(key)) projects.set(key, [])
        projects.get(key).push(item)
    }

    for (const leaves of projects.values()) {
        for (const leaf of [...leaves].sort(reverseCode)) {
            const chain = []
            let current = leaf
            const seen = new Set()
            while (current && !seen.has(String(current.id))) {
                chain.push(current)
                seen.add(String(current.id))
                current =
                    current.parent_activity_id != null
                        ? byId.get(String(current.parent_activity_id))
                        : null
            }
            let pathKey = ''
            chain.forEach((node, depth) => {
                const parentTreeId = depth === 0 ? null : pathKey
                pathKey = pathKey ? `${pathKey}>${node.id}` : String(node.id)
                const hasOrigin = depth < chain.length - 1
                result.push({
                    ...node,
                    depth,
                    tree_id: pathKey,
                    tree_parent_id: parentTreeId,
                    tree_has_children: hasOrigin,
                })
            })
        }
    }
    return result
}
export function planningMenu(locale, companyId, company, t = (key) => key) {
    const suffix = companyId ? `/${companyId}` : ''
    const items = [
        {
            id: 'projects',
            name: t('nav_menu.projects'),
            route: `/${locale}/dashboard/projects${suffix}`,
        },
    ]

    if (canPlanning(company, 'activity_view')) {
        items.push({
            id: 'activities',
            name: t('nav_menu.activities'),
            route: `/${locale}/dashboard/activities${suffix}`,
        })
        items.push({
            id: 'time_entries',
            name: t('nav_menu.time_entries'),
            route: `/${locale}/dashboard/time-entries${suffix}`,
        })
    }

    return items
}
export function descendants(items, id) {
    const result = new Set([String(id)])
    let changed = true
    while (changed) {
        changed = false
        for (const item of items)
            if (result.has(String(item.parent_activity_id)) && !result.has(String(item.id))) {
                result.add(String(item.id))
                changed = true
            }
    }
    return result
}
export function hierarchy(items) {
    const live = items
        .filter((item) => Number(item.status) !== 6)
        .sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }))
    const byId = new Map(live.map((item) => [String(item.id), item]))
    const visited = new Set(),
        result = []
    function walk(item, depth, level) {
        if (visited.has(String(item.id))) return
        visited.add(String(item.id))
        const children = live.filter(
            (child) => String(child.parent_activity_id) === String(item.id) && child.id !== item.id,
        )

        const displayLevel = item.code || level
        result.push({ ...item, depth, level: displayLevel, hasChildren: children.length > 0 })
        children.forEach((child, i) =>
            walk(
                child,
                depth + 1,
                `${displayLevel.replace(/\.0$/, '')}.${String(i + 1).padStart(2, '0')}`,
            ),
        )
    }
    const rootCounts = new Map()
    live.filter((item) => !byId.has(String(item.parent_activity_id))).forEach((item) => {
        const key = String(item.project_id)
        const number = (rootCounts.get(key) || 0) + 1
        rootCounts.set(key, number)
        walk(item, 0, `${number}.0`)
    })
    live.forEach((item) => {
        if (!visited.has(String(item.id))) walk(item, 0, `${result.length + 1}.0`)
    })
    return result
}
export const hours = (seconds) => {
    const minutes = Math.floor(Math.abs(Number(seconds) || 0) / 60)
    return `${seconds < 0 ? '-' : ''}${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
}
export const clock = (seconds) =>
    `${hours(seconds).padStart(5, '0')}:${String(Math.floor(Math.abs(seconds) % 60)).padStart(2, '0')}`
export const dateLabel = (value) =>
    value ? String(value).slice(0, 10).split('-').reverse().join('/') : '—'
// XML Spreadsheet 2003: XLS legível pelo Excel, com células String (sem fórmulas injetadas).
export function exportXls(filename, headers, rows) {
    const escape = (value) =>
        String(value ?? '')
            .replace(
                /[&<>"']/g,
                (c) =>
                    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c],
            )
            .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '')
    const xml =
        '<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Worksheet ss:Name="Dados"><Table>' +
        [headers, ...rows]
            .map(
                (row) =>
                    '<Row>' +
                    row
                        .map(
                            (value) =>
                                `<Cell><Data ss:Type="String">${escape(value)}</Data></Cell>`,
                        )
                        .join('') +
                    '</Row>',
            )
            .join('') +
        '</Table></Worksheet></Workbook>'
    const url = URL.createObjectURL(
        new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' }),
    )
    const a = document.createElement('a')
    a.href = url
    a.download = `${filename}.xls`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// O código pertence ao projeto; não depende da ordenação visual da tabela.
export function activityCode(items, data) {
    const live = items.filter(
        (a) => Number(a.status) !== 6 && String(a.project_id) === String(data.project_id),
    )
    const current = live.find((a) => String(a.id) === String(data.id))
    const tree = hierarchy(live)
    if (
        current &&
        String(current.parent_activity_id || '') === String(data.parent_activity_id || '')
    )
        return String(
            current.code || tree.find((a) => String(a.id) === String(current.id))?.level || '1.0',
        )
    const siblings = tree.filter(
        (a) =>
            String(a.parent_activity_id || '') === String(data.parent_activity_id || '') &&
            String(a.id) !== String(data.id),
    )
    const parent = tree.find((a) => String(a.id) === String(data.parent_activity_id))
    const next =
        Math.max(
            0,
            ...siblings.map(
                (a) => Number(parent ? a.level.split('.').at(-1) : a.level.split('.')[0]) || 0,
            ),
        ) + 1
    return parent
        ? `${parent.level.replace(/\.0$/, '')}.${String(next).padStart(2, '0')}`
        : `${next}.0`
}
export function activityHoursError(items, data) {
    const getHours = (obj) => estimatedSeconds(obj) / 3600

    const amount = getHours(data)
    if (!Number.isFinite(amount) || amount < 0) return 'estimated_hours_invalid'

    const live = items.filter(
        (a) => Number(a.status) !== 6 && String(a.project_id) === String(data.project_id),
    )

    const parent = live.find((a) => String(a.id) === String(data.parent_activity_id))
    const total = (rows) => rows.reduce((sum, a) => sum + getHours(a), 0)

    if (parent) {
        const siblings = live.filter(
            (a) =>
                String(a.parent_activity_id) === String(parent.id) &&
                String(a.id) !== String(data.id),
        )
        if (total(siblings) + amount > getHours(parent) + 1e-9) return 'child_hours_exceed_parent'
    }

    if (
        data.id &&
        total(live.filter((a) => String(a.parent_activity_id) === String(data.id))) > amount + 1e-9
    )
        return 'parent_hours_below_children'

    return ''
}


export function estimatedSeconds(data) {
    const value = data?.estimated_hours ?? data?.estimated_time ?? 0
    if (value === '') return 0
    const text = String(value).trim()
    const time = text.match(/^(\d+):([0-5]\d)(?::([0-5]\d))?$/)
    if (time) return Number(time[1]) * 3600 + Number(time[2]) * 60 + Number(time[3] || 0)
    const hours = Number(text.replace(',', '.'))
    return Number.isFinite(hours) && hours >= 0 ? Math.round(hours * 3600) : NaN
}

export function projectHoursError(project, activities, candidate = null) {
    // Filhas são validadas contra o pai por activityHoursError().
    if (candidate?.parent_activity_id) return ''
    if (!project) return 'project_not_found'

    // O backend não limita projetos sem horas estimadas definidas.
    if (project.estimated_hours == null && project.estimated_time == null) return ''

    const limit = estimatedSeconds(project)
    if (!Number.isFinite(limit)) return 'project_hours_invalid'

    const rows = activities.filter(
        (activity) =>
            Number(activity.status) !== 6 &&
            String(activity.project_id) === String(project.id) &&
            !activity.parent_activity_id &&
            (candidate?.id == null || String(activity.id) !== String(candidate.id)),
    )

    if (candidate && Number(candidate.status) !== 6) rows.push(candidate)

    const amounts = rows.map(estimatedSeconds)
    if (amounts.some((amount) => !Number.isFinite(amount))) return 'activity_hours_invalid'

    const total = amounts.reduce((sum, amount) => sum + amount, 0)
    const format = (seconds) =>
        `${Math.floor(seconds / 3600)}h ${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}min`

    return total > limit
        ? {
              key: 'project_hours_exceeded',
              params: { total: format(total), limit: format(limit) },
          }
        : ''
}
